import { PartialComponent } from 'packages/services/componentService';
import { EnrichedComponentCollection } from './types';

export interface ComponentOverviewState {
    collections: EnrichedComponentCollection[];
    collectionsPage: number;
    hasMoreCollections: boolean;
    isCollectionsLoading: boolean;

    components: PartialComponent[];
    componentsPage: number;
    componentsTotal: number;
    hasMoreComponents: boolean;
    isComponentsLoading: boolean;

    selectedCollection: EnrichedComponentCollection | null;
    refetchCounter: number;
}

export const initialState: ComponentOverviewState = {
    collections: [],
    collectionsPage: 0,
    hasMoreCollections: true,
    isCollectionsLoading: false,

    components: [],
    componentsPage: 0,
    componentsTotal: 0,
    hasMoreComponents: true,
    isComponentsLoading: false,

    selectedCollection: null,
    refetchCounter: 0,
};

type Action =
    | { type: 'RESET' }
    | { type: 'FETCH_COLLECTIONS_START' }
    | {
          type: 'FETCH_COLLECTIONS_SUCCESS';
          payload: {
              collections: EnrichedComponentCollection[];
              hasMore: boolean;
              page: number;
          };
      }
    | { type: 'FETCH_COMPONENTS_START' }
    | {
          type: 'FETCH_COMPONENTS_SUCCESS';
          payload: {
              components: PartialComponent[];
              total: number;
              hasMore: boolean;
              page: number;
          };
      }
    | { type: 'SELECT_COLLECTION'; payload: EnrichedComponentCollection }
    | { type: 'DESELECT_COLLECTION' }
    | { type: 'INCREMENT_COLLECTIONS_PAGE' }
    | { type: 'INCREMENT_COMPONENTS_PAGE' }
    | { type: 'REFETCH'; payload: { resetCollection: boolean } }
    | { type: 'UPDATE_COLLECTION_NAME'; payload: { id: number; name: string } }
    | { type: 'UPDATE_SELECTED_COLLECTION_NAME'; payload: { name: string } }
    | { type: 'FETCH_DONE'; payload: { target: 'collections' | 'components' } };

export type ComponentOverviewAction = Action;

export function reducer(state: ComponentOverviewState, action: Action): ComponentOverviewState {
    switch (action.type) {
        case 'RESET':
            return { ...initialState };

        case 'FETCH_COLLECTIONS_START':
            return { ...state, isCollectionsLoading: true, components: [], componentsTotal: 0 };

        case 'FETCH_COLLECTIONS_SUCCESS':
            return {
                ...state,
                collections:
                    action.payload.page === 0
                        ? action.payload.collections
                        : [...state.collections, ...action.payload.collections],
                hasMoreCollections: action.payload.hasMore,
                collectionsPage: action.payload.page,
                isCollectionsLoading: false,
            };

        case 'FETCH_COMPONENTS_START':
            return {
                ...state,
                isComponentsLoading: true,
                ...(!state.selectedCollection ? { collections: [], collectionsPage: 0 } : {}),
            };

        case 'FETCH_COMPONENTS_SUCCESS':
            return {
                ...state,
                components:
                    action.payload.page === 0
                        ? action.payload.components
                        : [...state.components, ...action.payload.components],
                componentsTotal: action.payload.total,
                hasMoreComponents: action.payload.hasMore,
                componentsPage: action.payload.page,
                isComponentsLoading: false,
            };

        case 'SELECT_COLLECTION':
            return {
                ...state,
                selectedCollection: action.payload,
                components: [],
                componentsPage: 0,
                componentsTotal: 0,
                hasMoreComponents: true,
                isComponentsLoading: false,
            };

        case 'DESELECT_COLLECTION':
            return {
                ...state,
                selectedCollection: null,
                components: [],
                componentsPage: 0,
                componentsTotal: 0,
                hasMoreComponents: true,
                isComponentsLoading: false,
            };

        case 'INCREMENT_COLLECTIONS_PAGE':
            return state.hasMoreCollections ? { ...state, collectionsPage: state.collectionsPage + 1 } : state;

        case 'INCREMENT_COMPONENTS_PAGE':
            return state.hasMoreComponents ? { ...state, componentsPage: state.componentsPage + 1 } : state;

        case 'REFETCH':
            return {
                ...state,
                collections: [],
                components: [],
                collectionsPage: 0,
                componentsPage: 0,
                componentsTotal: 0,
                hasMoreCollections: true,
                hasMoreComponents: true,
                refetchCounter: state.refetchCounter + 1,
                ...(action.payload.resetCollection ? { selectedCollection: null } : {}),
            };

        case 'UPDATE_COLLECTION_NAME':
            return {
                ...state,
                collections: state.collections.map((col) =>
                    col.id === action.payload.id ? { ...col, name: action.payload.name } : col,
                ),
            };

        case 'UPDATE_SELECTED_COLLECTION_NAME':
            return state.selectedCollection
                ? {
                      ...state,
                      selectedCollection: { ...state.selectedCollection, name: action.payload.name },
                  }
                : state;

        case 'FETCH_DONE': {
            if (action.payload.target === 'collections') {
                return { ...state, isCollectionsLoading: false };
            }

            return { ...state, isComponentsLoading: false };
        }

        default:
            return state;
    }
}
